import { useState, useCallback, useMemo } from 'react';
import { Question, GameAnswer, Difficulty, Category, GameMode } from '../types';
import { calculatePoints, calculateAccuracy, getGameQuestions } from '../utils/gameLogic';

interface UseGameOptions {
  questions: Question[];
  mode: GameMode;
  category?: Category | 'all';
  difficulty?: Difficulty | 'mixed';
  questionsPerGame?: number;
  timePerQuestion?: number; // in seconds
  onGameComplete?: (result: GameResult) => void;
}

interface GameResult {
  score: number;
  accuracy: number;
  totalTime: number;
  answers: GameAnswer[];
  correctCount: number;
  incorrectCount: number;
}

interface UseGameReturn {
  // State
  currentQuestionIndex: number;
  currentQuestion: Question | null;
  answers: GameAnswer[];
  score: number;
  streak: number;
  longestStreak: number;
  isGameActive: boolean;
  isGameComplete: boolean;
  progress: number;

  // Actions
  submitAnswer: (selectedAnswer: number, timeSpent: number) => void;
  nextQuestion: () => void;
  resetGame: () => void;
  startGame: () => void;

  // Computed
  correctCount: number;
  incorrectCount: number;
  accuracy: number;
  totalQuestions: number;
  gameResult: GameResult | null;
}

/**
 * Custom hook for managing game state and logic
 * @param options - Game configuration options
 * @returns Game state and control functions
 */
export const useGame = ({
  questions: allQuestions,
  mode,
  category = 'all',
  difficulty = 'mixed',
  questionsPerGame = 10,
  timePerQuestion = 15,
  onGameComplete,
}: UseGameOptions): UseGameReturn => {
  // Game questions (randomized and filtered)
  const [gameQuestions, setGameQuestions] = useState<Question[]>([]);

  // Current state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<GameAnswer[]>([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [isGameActive, setIsGameActive] = useState(false);
  const [isGameComplete, setIsGameComplete] = useState(false);

  // Current question
  const currentQuestion = useMemo(() => {
    if (currentQuestionIndex >= gameQuestions.length) {
      return null;
    }
    return gameQuestions[currentQuestionIndex];
  }, [currentQuestionIndex, gameQuestions]);

  // Progress percentage
  const progress = useMemo(() => {
    if (gameQuestions.length === 0) return 0;
    return Math.round((currentQuestionIndex / gameQuestions.length) * 100);
  }, [currentQuestionIndex, gameQuestions.length]);

  // Correct and incorrect counts
  const correctCount = useMemo(() => {
    return answers.filter((a) => a.isCorrect).length;
  }, [answers]);

  const incorrectCount = useMemo(() => {
    return answers.filter((a) => !a.isCorrect).length;
  }, [answers]);

  // Accuracy percentage
  const accuracy = useMemo(() => {
    return calculateAccuracy(correctCount, answers.length);
  }, [correctCount, answers.length]);

  // Total questions
  const totalQuestions = gameQuestions.length;

  // Game result
  const gameResult = useMemo((): GameResult | null => {
    if (!isGameComplete) return null;

    const totalTime = answers.reduce((sum, a) => sum + a.timeSpent, 0);

    return {
      score,
      accuracy,
      totalTime,
      answers,
      correctCount,
      incorrectCount,
    };
  }, [isGameComplete, score, accuracy, answers, correctCount, incorrectCount]);

  /**
   * Start a new game
   */
  const startGame = useCallback(() => {
    // Get randomized questions for this game
    const selectedQuestions = getGameQuestions(
      allQuestions,
      category,
      difficulty,
      questionsPerGame
    );

    setGameQuestions(selectedQuestions);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setScore(0);
    setStreak(0);
    setLongestStreak(0);
    setIsGameActive(true);
    setIsGameComplete(false);
  }, [allQuestions, category, difficulty, questionsPerGame]);

  /**
   * Submit an answer for the current question
   */
  const submitAnswer = useCallback(
    (selectedAnswer: number, timeSpent: number) => {
      if (!currentQuestion || !isGameActive) return;

      const isCorrect = selectedAnswer === currentQuestion.correctAnswer;

      // Update streak
      let newStreak = streak;
      let newLongestStreak = longestStreak;

      if (isCorrect) {
        newStreak = streak + 1;
        newLongestStreak = Math.max(longestStreak, newStreak);
      } else {
        newStreak = 0;
      }

      setStreak(newStreak);
      setLongestStreak(newLongestStreak);

      // Calculate points (only if correct)
      const points = isCorrect
        ? calculatePoints(
            currentQuestion.difficulty,
            Math.max(0, timePerQuestion - timeSpent / 1000),
            newStreak
          )
        : 0;

      // Update score
      setScore((prevScore) => prevScore + points);

      // Record answer
      const answer: GameAnswer = {
        questionId: currentQuestion.id,
        selectedAnswer,
        isCorrect,
        timeSpent,
        points,
      };

      setAnswers((prevAnswers) => [...prevAnswers, answer]);
    },
    [currentQuestion, isGameActive, streak, longestStreak, timePerQuestion]
  );

  /**
   * Move to next question or complete game
   */
  const nextQuestion = useCallback(() => {
    if (!isGameActive) return;

    const nextIndex = currentQuestionIndex + 1;

    if (nextIndex >= gameQuestions.length) {
      // Game complete
      setIsGameActive(false);
      setIsGameComplete(true);

      // Call completion callback with a slight delay to ensure state has updated
      if (onGameComplete) {
        setTimeout(() => {
          const totalTime = answers.reduce((sum, a) => sum + a.timeSpent, 0);
          // Calculate final counts from the answers array directly
          const finalCorrectCount = answers.filter(a => a.isCorrect).length;
          const finalIncorrectCount = answers.filter(a => !a.isCorrect).length;
          const finalAccuracy = calculateAccuracy(finalCorrectCount, answers.length);

          const result: GameResult = {
            score,
            accuracy: finalAccuracy,
            totalTime,
            answers,
            correctCount: finalCorrectCount,
            incorrectCount: finalIncorrectCount,
          };
          onGameComplete(result);
        }, 0);
      }
    } else {
      // Move to next question
      setCurrentQuestionIndex(nextIndex);
    }
  }, [
    currentQuestionIndex,
    gameQuestions.length,
    isGameActive,
    answers,
    score,
    onGameComplete,
  ]);

  /**
   * Reset the game to initial state
   */
  const resetGame = useCallback(() => {
    setGameQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setScore(0);
    setStreak(0);
    setLongestStreak(0);
    setIsGameActive(false);
    setIsGameComplete(false);
  }, []);

  return {
    // State
    currentQuestionIndex,
    currentQuestion,
    answers,
    score,
    streak,
    longestStreak,
    isGameActive,
    isGameComplete,
    progress,

    // Actions
    submitAnswer,
    nextQuestion,
    resetGame,
    startGame,

    // Computed
    correctCount,
    incorrectCount,
    accuracy,
    totalQuestions,
    gameResult,
  };
};
